import axios from "./axios_interceptor";
import { get_jwt_token, loginRedirect } from "./helper_funtions";
import { encodeResourceParams, decodeResourceParams } from "../utils/urlEncoder";


const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const encodedData = urlParams.get("data");
  
  if (encodedData) {
    return decodeResourceParams(encodedData) || {};
  }
  return {};
};

// Helper function to get database GUI API params from current URL and encode them
const getDatabaseGuiParams = (additionalParams = {}) => {
  const params = getUrlParams();
  const mergedParams = {
    ...params,
    ...additionalParams,
  };
  const encodedData = encodeResourceParams(mergedParams);
  return { data: encodedData };
};

// Module-level url_params for backward compatibility
const url_params = getUrlParams();

// Common URL string is no longer used in the same way, removing legacy construction
const common_url = ""; // Deprecated, strictly use encoded params


export const getDatabaseType = async (
  resourceId, 
  systemUserId,
  appType
) => {
  try {
    const token = await get_jwt_token();
    loginRedirect(token);
    
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
        }),
      },
    };
    
    const url = "/apps/database-type";
    const response = await axios.get(url, config);
    return { data: response.data, error: null };
  } catch (e) {
  if (e.response && e.response.data) {
      return {
        data: null,
        error: {
          error_code: e.response.data.error_code,
          error_message: e.response.data.error_message,
          http_status: e.response.status,
        },
      };
    }
    return {
      data: null,
      error: {
        error_message: e.message || "Unknown error",
      },
    };
  }
};

/* ====================== Database Operations ====================== */


export const getDatabases = async (
  resourceId, 
  systemUserId
) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: { 
        ...getDatabaseGuiParams({
          resource_id: resourceId, 
          systemuser_id: systemUserId,
        }),
      },
    };
    
    const url = "/apps/databases-operations/";
    const response = await axios.get(url, config);
    return { data: response.data, error: null };
  } catch (e) {
    const errorMsg = e.response ? e.response.data.errors : e.message;
    return { data: null, error: errorMsg };
  }
};

export const createDatabase = async (
  resourceId,
  systemUserId,
  databaseName,
  resourceAccessID
) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
          ...(resourceAccessID && { resource_access_report_id: resourceAccessID }),
        }),
      },
    };

    const requestData = {
      database_name: databaseName,
    };

    const url = "/apps/databases-operations/";
    const response = await axios.post(url, requestData, config);
    return { data: response.data, error: null };
  } catch (e) {
    let errorMessage = "Failed to create database";
    
    if (e.response?.data) {
      if (e.response.data.message) {
        if (Array.isArray(e.response.data.message)) {
          errorMessage = e.response.data.message.join(', ');
        } else {
          errorMessage = e.response.data.message;
        }
      } else if (e.response.data.errors) {
        if (Array.isArray(e.response.data.errors)) {
          errorMessage = e.response.data.errors.join(', ');
        } else {
          errorMessage = JSON.stringify(e.response.data.errors);
        }
      } else if (typeof e.response.data === 'string') {
        errorMessage = e.response.data;
      } else {
        errorMessage = JSON.stringify(e.response.data);
      }
    } else if (e.message) {
      errorMessage = e.message;
    }
    
    return {
      data: null,
      error: { message: errorMessage },
    };
  }
};

export const renameDatabase = async (
  resourceId,
  systemUserId,
  oldDbName,
  newDbName,
  resourceAccessID
) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const body = {
      old_db_name: Array.isArray(oldDbName) ? oldDbName[0] : oldDbName,
      new_db_name: newDbName,
    };
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
          ...(resourceAccessID && { resource_access_report_id: resourceAccessID }),
        }),
      },
    };
    
    const url = "/apps/databases-operations/";
    const response = await axios.put(url, body, config);
    return { data: response.data, error: null };
  } catch (error) {
    const errorMessage = error.response ? error.response.data : error.message;
    return { data: null, error: errorMessage };
  }
};

export const dropDatabase = async (
  resourceId, 
  systemUserId, 
  databaseName,
  resourceAccessID
) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    
    // Ensure databaseName is a string
    const dbName = Array.isArray(databaseName) ? databaseName[0] : databaseName;
    
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: { 
        ...getDatabaseGuiParams({
          resource_id: resourceId, 
          systemuser_id: systemUserId,
          ...(resourceAccessID && { resource_access_report_id: resourceAccessID }),
        }),
      },
      data: {
        database_name: dbName,
      },
    };
    
    const url = "/apps/databases-operations/";
    const response = await axios.delete(url, config);
    return { message: response.data.message, error: null };
  } catch (e) {
    return {
      message: null,
      error: e.response ? e.response.data.errors : { message: [e.message] },
    };
  }
};

export const backupDatabase = async (
  resourceId, 
  systemUserId, 
  databaseName
) => {
  try {
    const token = get_jwt_token();
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
        }),
      },
      responseType: "blob",
    };
    
    const requestData = {
      type: "database",
      action: "backup",
      database_name: databaseName,
    };
    
    const url = "/apps/backup-restore/";
    const response = await axios.post(url, requestData, config);
    return { success: true, message: response.data?.message };
  } catch (error) {
    let errorMessage;
    if (error.response && error.response.data) {
      // Prefer structured backend errors if available
      errorMessage =
        error.response.data.errors || error.response.data.message || error.response.data;
    } else {
      errorMessage = error.message || "Unknown error occurred while backing up the database.";
    }
    return { success: false, error: errorMessage };
  }
};

export const restoreDatabase = async (
  resourceId,
  systemUserId,
  databaseName,
  sqlFile
) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const formData = new FormData();
    formData.append("type", "database");
    formData.append("action", "restore");
    formData.append("database_name", databaseName);
    formData.append("sql_file", sqlFile);
    
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
        }),
      },
    };
    
    const url = "/apps/backup-restore/";
    const response = await axios.post(url, formData, config);
    
    return { success: true, message: response.data?.message };
  } catch (error) {
    let errorMessage;
    if (error.response && error.response.data) {
      errorMessage =
        error.response.data.errors || error.response.data.message || error.response.data;
    } else {
      errorMessage = error.message || "Unknown error occurred while restoring the database.";
    }
    return { success: false, error: errorMessage };
  }
};

/* ====================== Table Operations ====================== */

export const getTableCount = async (
  resourceId, 
  systemUserId, 
  dbName
) => {
  if (!dbName) {
    throw new Error("Database name is required");
  }
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          app_id: resourceId,
          systemuser_id: systemUserId,
        }),
      },
    };
    const response = await axios.get(`/tables/${dbName}/` + common_url, config);
    return response.data.table_count;
  } catch (error) {
    const errorMessage = error.response ? error.response.data : error.message;
  }
};

export const getTables = async (
  resourceId, 
  systemUserId, 
  database
) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
          database_name: database,
        }),
      },
    };
    
    const url = "/apps/tables-operations/";
    const response = await axios.get(url, config);
    return { data: response.data, error: null };
  } catch (e) {
    return {
      data: null,
      error: e.response ? e.response.data.errors : e.message,
    };
  }
};

export const getColumns = async (
  resourceId,
  systemUserId,
  database,
  tableName
) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
          database_name: database,
          table_name: tableName,
        }),
      },
    };
    const url = "/apps/columns-operations/";
    const response = await axios.get(url, config);
    return { data: response.data, error: null };
  } catch (e) {
    return {
      data: null,
      error: e.response ? e.response.data.errors : e.message,
    };
  }
};

export const createTable = async (
  resourceId,
  systemUserId,
  databaseName,
  tableName,
  columns,
  resourceAccessID
) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    
    const requestData = {
      app_id: resourceId,
      systemuser_id: systemUserId,
      db_name: databaseName,
      table_name: tableName,
      columns: columns,
    };

    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
          ...(resourceAccessID && { resource_access_report_id: resourceAccessID }),
        }),
      },
    };
    
    const url = "/apps/tables-operations/";
    const response = await axios.post(url, requestData, config);
    
    return { message: response.data.message, error: null };
  } catch (e) {
    let errorMessage =
      e.response && e.response.data ? e.response.data.error : e.message;
    
    // Error details handled silently
    
    return {
      message: null,
      error: e.response ? e.response.data : { message: errorMessage },
    };
  }
};

export const renameTable = async (
  resourceId,
  systemUserId,
  dbName,
  oldTableName,
  newTableName,
  resourceAccessID
) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const body = {
      app_id: resourceId,
      systemuser_id: systemUserId,
      db_name: dbName,
      old_table_name: oldTableName,
      new_table_name: newTableName,
    };
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
          ...(resourceAccessID && { resource_access_report_id: resourceAccessID }),
        }),
      },
    };
    const url = "/apps/tables-operations/";
    const response = await axios.put(url, body, config);
    return { data: response.data, error: null };
  } catch (error) {
    const errorMessage = error.response ? error.response.data : error.message;
    return { data: null, error: errorMessage };
  }
};

export const addColumnToTable = async (
  resourceId,
  systemUserId,
  appType,
  databaseName,
  tableName,
  columnName,
  columnType,
  columnLength,
  resourceAccessID
) => {
  try {    
    const token = get_jwt_token();
    loginRedirect(token);
    const tableNameValue = Array.isArray(tableName) ? tableName[0] : tableName;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
          ...(resourceAccessID && { resource_access_report_id: resourceAccessID }),
        }),
      },
    };
    // Format column type with length if needed
    let formattedColumnType = columnType;
    if (columnLength && ["VARCHAR", "CHAR"].includes(columnType)) {
      formattedColumnType = `${columnType}(${columnLength})`;
    }
    
    const data = {
      database_name: databaseName,
      table_name: tableNameValue,
      column_name: columnName,
      column_type: formattedColumnType,
    };
    
    const url = "/apps/columns-operations/";
    const response = await axios.put(url, data, config);
    return { data: response.data, error: null };
  } catch (e) {
    let errorMsg;
    if (e.response?.data) {
      if (e.response.data.errors) {
        errorMsg = e.response.data.errors;
      } else if (e.response.data.error) {
        // Handle singular 'error' field from backend
        errorMsg = { message: [e.response.data.error] };
      } else if (e.response.data.message) {
        errorMsg = { message: [e.response.data.message] };
      } else {
        errorMsg = { message: [JSON.stringify(e.response.data)] };
      }
    } else {
      errorMsg = { message: [e.message] };
    }
    
    return { data: null, error: errorMsg };
  }
};

export const dropColumnFromTable = async (
  resourceId,
  systemUserId,
  databaseName,
  tableName,
  columnName,
  appType,
  resourceAccessID
) => {
  try {    
    const token = get_jwt_token();
    loginRedirect(token);
    const tableNameValue = Array.isArray(tableName) ? tableName[0] : tableName;
    const databaseNameValue = Array.isArray(databaseName) ? databaseName[0] : databaseName;
    const columnNameValue = Array.isArray(columnName) ? columnName[0] : columnName;
    
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
          ...(resourceAccessID && { resource_access_report_id: resourceAccessID }),
        }),
      },
      data: {
        database_name: databaseNameValue,
        table_name: tableNameValue,
        column_name: columnNameValue,
      },
    };
    
    const url = "/apps/columns-operations/";
    const response = await axios.delete(url, config);
    return { data: response.data, error: null };
  } catch (e) {
    let errorMsg;
    if (e.response?.data) {
      if (e.response.data.errors) {
        errorMsg = e.response.data.errors;
      } else if (e.response.data.message) {
        errorMsg = { message: [e.response.data.message] };
      } else {
        errorMsg = { message: [JSON.stringify(e.response.data)] };
      }
    } else {
      errorMsg = { message: [e.message] };
    }
    
    return { data: null, error: errorMsg };
  }
};

export const dropTable = async (
  resourceId,
  systemUserId,
  databaseName,
  tableName,
  resourceAccessID
) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const tableNameValue = Array.isArray(tableName) ? tableName[0] : tableName;
    const databaseNameValue = Array.isArray(databaseName)
      ? databaseName[0]
      : databaseName;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
          ...(resourceAccessID && { resource_access_report_id: resourceAccessID }),
        }),
      },
      data: {
        db_name: databaseNameValue,
        table_name: tableNameValue,
      },
    };
    const url = "/apps/tables-operations/";
    const response = await axios.delete(url, config);
    return { message: response.data.message, error: null };
  } catch (e) {
    return {
      message: null,
      error: e.response ? e.response.data.errors : { message: [e.message] },
    };
  }
};

export const backupTable = async (
  resourceId,
  systemUserId,
  databaseName,
  tableName
) => {
  try {
    const token = get_jwt_token();
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
        }),
      },
      responseType: "blob",
    };
    
    const requestData = {
      type: "table",
      action: "backup",
      database_name: databaseName,
      table_name: tableName,
    };
    
    const url = "/apps/backup-restore/";
    const response = await axios.post(url, requestData, config);
    return { success: true, data: response.data };
  } catch (error) {
    const errorMessage = error.response ? error.response.data : error.message;
    return { success: false, error: errorMessage };
  }
};

export const restoreTable = async (
  resourceId,
  systemUserId,
  databaseName,
  tableName,
  sqlFile
) => {
  try {
    const formData = new FormData();
    formData.append("type", "table");
    formData.append("action", "restore");
    formData.append("database_name", databaseName);
    formData.append("table_name", tableName);
    formData.append("sql_file", sqlFile);
    
    const token = get_jwt_token();
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
        }),
      },
    };
    
    
    const url = "/apps/backup-restore/";
    const response = await axios.post(url, formData, config);
    
    return { success: true, message: response.data?.message };
  } catch (error) {
    let errorMessage;
    if (error.response && error.response.data) {
      errorMessage =
        error.response.data.errors || error.response.data.message || error.response.data;
    } else {
      errorMessage = error.message || "Unknown error occurred while restoring the table.";
    }
    return { success: false, error: errorMessage };
  }
};

export const truncateData = async (
  resourceId,
  systemUserId,
  dbName,
  tableName,
  resourceAccessID
) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
          ...(resourceAccessID && { resource_access_report_id: resourceAccessID }),
        }),
      },
      data: {
        action: "truncate",
        db_name: Array.isArray(dbName) ? dbName[0] : dbName,
        table_name: Array.isArray(tableName) ? tableName[0] : tableName,
      },
    };
    const url = "/apps/tables-operations/";
    const response = await axios.delete(url, config);
    return { data: response.data, error: null };
  } catch (e) {
    return {
      data: null,
      error: e.response ? e.response.data.errors : e.message,
    };
  }
};

export const importTable = async (
  resourceId, 
  systemUserId, 
  formData
) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
        }),
      },
    };
    const url = "/apps/import-export-table-data/";
    const response = await axios.post(url, formData, config);
    return { data: response.data, error: null };
  } catch (e) {
    const errorMessage = e.response ? e.response.data : e.message;
    if (e.response && e.response.data) {
    }
    return { data: null, error: errorMessage };
  }
};

export const exportTable = async (
  resourceId,
  systemUserId,
  databaseName,
  tableName
) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const ExporttableName = Array.isArray(tableName) ? tableName[0] : tableName;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
          db_name: databaseName,
          table_name: ExporttableName,
        }),
      },
      responseType: "blob",
    };
    
    const url = "/apps/import-export-table-data/";
    const response = await axios.get(url, config);
    const blob = new Blob([response.data], { type: "text/csv" });
    return { blob, message: "Export successful", error: null };
  } catch (error) {
    return {
      blob: null,
      message: null,
      error: error.response ? error.response.data : error.message,
    };
  }
};

/* ====================== Data and Query Operations ====================== */

export const viewTableData = async (
  resourceId,
  systemUserId,
  databaseName,
  tableName
) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const tableNameValue = Array.isArray(tableName) ? tableName[0] : tableName;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
          database_name: databaseName,
          table_name: tableNameValue,
        }),
      },
    };
    
    const url = "/apps/table-data/";
    const response = await axios.get(url, config);
    return {
      data: response.data,
      columns: response.columns,
      query: response.query,
      error: null,
    };
  } catch (e) {
    return {
      error: e.response ? e.response.data.errors : e.message,
    };
  }
};

export const executeQuery = async (
  resourceId,
  systemUserId,
  query,
  databaseName,
  resourceAccessID
) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    
    // Use the passed resourceAccessID parameter
    const resource_access_report_id = resourceAccessID;
    
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
          ...(resource_access_report_id && { resource_access_report_id: resource_access_report_id }),
        }),
      },
    };
    
    const url = "/apps/execute-query/";
    
    const response = await axios.post(
      url,
      {
        app_id: resourceId,
        systemuser_id: systemUserId,
        query: query,
        database: databaseName,
      },
      config
    );
    if (response.data && !response.data.error) {
      return {
        message: response.data.message || "Query executed successfully.",
        data: response.data.data || [],
        columns: response.data.columns || [],
        error: null,
      };
    } else {
      const errorMsg =
        response.data.error ||
        "Unknown error occurred while executing the query.";
      return {
        message: null,
        data: null,
        columns: [],
        error: errorMsg,
      };
    }
  } catch (e) {
    let errorMessage =
      e.response && e.response.data ? e.response.data.error : e.message;
    return {
      data: null,
      columns: [],
      error:
        errorMessage || "An unknown error occurred while executing the query.",
    };
  }
};


export const closeDatabaseGuiConnection = async (resourceAccessId) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);

    const requestBody = getDatabaseGuiParams({
      resource_access_report_id: resourceAccessId,
    });
    
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      keepalive: true,
    };
    
    const url = "/apps/close_gui_connection/";
    const response = await axios.post(url, requestBody, config);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error };
  }
};

export const updateTableData = async (
  resourceId,
  systemUserId,
  databaseName,
  tableName,
  updateData,
  resourceAccessID
) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    
    const resource_access_report_id = resourceAccessID;
    
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...getDatabaseGuiParams({
          resource_id: resourceId,
          systemuser_id: systemUserId,
          ...(resource_access_report_id && { resource_access_report_id: resource_access_report_id }),
        }),
      },
    };
    
    const url = "/apps/table-data/";
    
    const response = await axios.put(
      url,
      {
        app_id: resourceId,
        systemuser_id: systemUserId,
        database: databaseName,
        table: tableName,
        update_data: updateData,
      },
      config
    );
    
    if (response.data && !response.data.error) {
      return {
        message: response.data.message || "Data updated successfully.",
        data: response.data.data || [],
        error: null,
      };
    } else {
      const errorMsg =
        response.data.error ||
        "Unknown error occurred while updating the data.";
      return {
        message: null,
        data: null,
        error: errorMsg,
      };
    }
  } catch (e) {
    let errorMessage =
      e.response && e.response.data ? e.response.data.error : e.message;
    return {
      data: null,
      error:
        errorMessage || "An unknown error occurred while updating the data.",
    };
  }
};